import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '@/shared/context/AuthContext'
import { ApiError } from '@/shared/api/rest/client'
import {
    DuplicateAction,
    ParentDuplicatesService
} from '@/shared/api/rest/services/parent-duplicates.service'
import { useAlertService } from '@/shared/lib/useAlertService'

/**
 * Turn a server-side failure into something an admin can act on.
 *
 * Nest answers an unhandled exception with `{ message: 'Internal server error' }`,
 * which the client faithfully surfaces — and which tells the person who clicked
 * the button nothing at all, least of all whether their data changed. A 5xx is
 * never the caller's fault, so say so and name the likely cause: detection
 * writes a `duplicate_reason` sized for the post-migration column, so an
 * environment running this code against the old schema fails here every time.
 */
const serverErrorMessage = (error: Error, fallback: string, aftermath: string) => {
    if (error instanceof ApiError && error.status >= 500) {
        return `${fallback} — the server failed, not your request. ${aftermath} If it keeps failing, check that the latest database migrations have run on this environment.`
    }
    return error.message || fallback
}

const SUCCESS_MESSAGE: Record<DuplicateAction, string> = {
    accept: 'Primary record kept — the secondary account has been merged into it.',
    keep_duplicate:
        'Secondary record kept — the primary account has been merged into it.',
    reject: 'Marked as not a duplicate. Both accounts have been left as they are.'
}

/**
 * Resolve a suspected duplicate, and re-run detection on demand.
 *
 * Every list that could be holding a pair is invalidated on success: the admin
 * grids, the parent's own review page and the notification feed all read
 * through those keys, and a merge also deletes a parent row, so the hydrated
 * `['parent', id]` entries have to go too.
 */
export const useDuplicateActions = () => {
    const alertService = useAlertService()
    const queryClient = useQueryClient()
    const { user } = useAuth()

    const invalidateAll = () => {
        queryClient.invalidateQueries({ queryKey: ['parent-duplicates'] })
        queryClient.invalidateQueries({ queryKey: ['parent'] })
        queryClient.invalidateQueries({ queryKey: ['parent-me'] })
        queryClient.invalidateQueries({ queryKey: ['parents-by-name'] })
        queryClient.invalidateQueries({ queryKey: ['parent-directory'] })
        queryClient.invalidateQueries({ queryKey: ['registry-parents'] })
    }

    const mutation = useMutation({
        mutationFn: ({
            id,
            action,
            notes
        }: {
            id: string
            action: DuplicateAction
            notes?: string
        }) => {
            // `processedBy` is a plain column, not a FK, but recording who
            // decided is the whole point of the audit trail — so refuse rather
            // than write an empty string.
            if (!user?._id) {
                return Promise.reject(
                    new Error('Your session has no user id — sign in again and retry.')
                )
            }
            return ParentDuplicatesService.process(id, {
                action,
                userId: user._id,
                notes: notes?.trim() || undefined
            })
        },
        onSuccess: (_data, variables) => {
            alertService.success(SUCCESS_MESSAGE[variables.action])
            invalidateAll()
        },
        onError: (error: Error) =>
            alertService.error(
                serverErrorMessage(
                    error,
                    'Failed to resolve the duplicate',
                    // The backend stamps and merges in one transaction, so a 5xx
                    // should mean nothing was written — but "should" is not
                    // "did", and re-merging the wrong way is unrecoverable.
                    'Reload and check whether it went through before retrying.'
                )
            )
    })

    const refresh = useMutation({
        mutationFn: () => ParentDuplicatesService.refresh(),
        onSuccess: ({ newRecords, updatedRecords }) => {
            invalidateAll()

            const found = newRecords ?? 0
            const refreshed = updatedRecords ?? 0
            const plural = (count: number, noun: string) =>
                `${count} ${count === 1 ? noun : `${noun}s`}`

            if (found === 0 && refreshed === 0) {
                alertService.success(
                    'Detection complete — no new suspected duplicates found.'
                )
                return
            }

            // `updatedRecords` counts pairs whose reason/score were rewritten
            // rather than pairs added, so it is reported separately — otherwise
            // the first run after the audit fix, which reconciles every legacy
            // reason string at once, reads as hundreds of new duplicates.
            if (found === 0) {
                alertService.success(
                    `Detection complete — no new suspected duplicates. ${plural(
                        refreshed,
                        'existing pair'
                    )} refreshed.`
                )
                return
            }

            alertService.success(
                `Detection complete — ${plural(found, 'new suspected duplicate')} found${
                    refreshed > 0
                        ? `, ${plural(refreshed, 'existing pair')} refreshed`
                        : ''
                }.`
            )
        },
        onError: (error: Error) =>
            alertService.error(
                serverErrorMessage(
                    error,
                    'Failed to run duplicate detection',
                    // The detection query is a single statement, so a failure
                    // leaves the table exactly as it was.
                    'Nothing was changed, so retrying is safe.'
                )
            )
    })

    return {
        resolve: mutation.mutateAsync,
        rerunDetection: refresh.mutateAsync,
        pending: mutation.isPending,
        refreshing: refresh.isPending
    }
}
